import { newId } from '@quizee/events/id';
import type { ClientEvent } from '@quizee/events';
import type { Consent } from './consent';
import type { Identity } from './identity';
import type { Assert, Extends } from './types';
import { referrerHost, sanitisePageUrl } from './url';

/**
 * The client half of the event envelope, written out by hand.
 *
 * `_NoDrift` at the bottom checks this union against `clientEventSchema` at compile time, which is
 * the only reason it is safe to hand build these rather than validate them with the real schema.
 * Everything here is snake_case because these records end up in parquet and are read with SQL.
 */
export interface PageContext {
  readonly page_url: string;
  readonly page_type: 'article';
  readonly article_id?: string | undefined;
  readonly referrer_host?: string | undefined;
  readonly locale?: string | undefined;
  readonly viewport_width?: number | undefined;
  /**
   * Where on the page the publisher put this embed, from `data-quizee-ref`.
   *
   * It answers "does the poll halfway down do better than the one at the end", which is a question
   * about placement rather than about a reader. It must never carry personal data: it is written
   * into a template by a publisher, it lands on every event, and nothing downstream would know to
   * treat it as an identifier. `placementRef` below is what enforces that, by refusing anything
   * that is not a short slug.
   */
  readonly placement_ref?: string | undefined;
}

export interface EventBase {
  readonly interaction_id: string;
  readonly interaction_version: number;
  readonly identity: Identity;
  readonly consent: Consent;
  readonly context: PageContext;
}

/**
 * The widget no longer emits one of these: views are counted from the CDN access logs instead, per
 * ARCHITECTURE D22. The variant stays in the union below, and this builder with it, because the
 * shared schema still carries the type and the archive holds years of them, so the hand written
 * mirror in this file has to keep matching what `clientEventSchema` says an impression looks like.
 */
export interface ImpressionData {
  readonly visible_ms?: number | undefined;
}

/**
 * Exactly one answer shape per event, mirroring the cross field rule in `checkCrossFieldRules`.
 *
 * Modelling it as a union rather than three optional fields is what stops an answer being built
 * with none of them, or with two, and being rejected at ingest with the reader none the wiser.
 * `value_text_ref` is here because the schema has it; the widget never constructs one, because a
 * free text answer goes to the submissions store and is referenced by the submit event instead.
 */
export type AnswerShape =
  | { readonly selected_option_ids: string[] }
  | { readonly value_number: number }
  | { readonly value_text_ref: string };

export type AnswerData = {
  readonly question_id: string;
  readonly question_index: number;
  readonly response_ms?: number | undefined;
  /** What the widget told the reader. Advisory: the projector recomputes scoring from the config. */
  readonly client_marked_correct?: boolean | undefined;
} & AnswerShape;

export interface CompleteData {
  readonly duration_ms?: number | undefined;
  /** Trivia. Client reported, recomputed at projection time for the same reason as above. */
  readonly score?: number | undefined;
  readonly max_score?: number | undefined;
  /** Personality. */
  readonly outcome_id?: string | undefined;
}

/** A survey or form was submitted. The values themselves are behind the reference. */
export interface SubmitData {
  readonly submission_ref: string;
  readonly field_count: number;
}

export type WidgetEvent = EventBase & {
  readonly event_id: string;
  readonly occurred_at: string;
} & (
    | { readonly event_type: 'impression'; readonly data: ImpressionData }
    | { readonly event_type: 'start'; readonly data: Record<string, never> }
    | { readonly event_type: 'answer'; readonly data: AnswerData }
    | { readonly event_type: 'complete'; readonly data: CompleteData }
    | { readonly event_type: 'submit'; readonly data: SubmitData }
  );

type _NoDrift = Assert<Extends<WidgetEvent, ClientEvent>>;

/** ISO 8601 UTC with milliseconds and a Z suffix, which is what `isoTimestamp` insists on. */
export function isoNow(now: Date = new Date()): string {
  return now.toISOString();
}

function envelope(base: EventBase, now: Date | undefined) {
  return {
    event_id: newId('event'),
    occurred_at: isoNow(now ?? new Date()),
    interaction_id: base.interaction_id,
    interaction_version: base.interaction_version,
    identity: base.identity,
    consent: base.consent,
    context: base.context,
  };
}

export function impressionEvent(
  base: EventBase,
  data: ImpressionData = {},
  now?: Date,
): WidgetEvent {
  return { ...envelope(base, now), event_type: 'impression', data };
}

export function startEvent(base: EventBase, now?: Date): WidgetEvent {
  return { ...envelope(base, now), event_type: 'start', data: {} };
}

export function answerEvent(base: EventBase, data: AnswerData, now?: Date): WidgetEvent {
  return { ...envelope(base, now), event_type: 'answer', data };
}

export function completeEvent(base: EventBase, data: CompleteData = {}, now?: Date): WidgetEvent {
  return { ...envelope(base, now), event_type: 'complete', data };
}

export function submitEvent(base: EventBase, data: SubmitData, now?: Date): WidgetEvent {
  return { ...envelope(base, now), event_type: 'submit', data };
}

const MAX_ARTICLE_ID = 200;
const MAX_LOCALE = 35;
const MAX_PLACEMENT_REF = 64;

/**
 * A slug naming where the embed sits, and nothing else.
 *
 * The shape is narrow on purpose. This value is publisher authored, so the widget cannot know what
 * somebody will eventually put in the attribute, and it travels on every event and on a signup. An
 * address, a reader id or a url with a query string in it would all be personal data arriving in a
 * field nothing downstream treats as personal data, so anything that is not a short slug is
 * dropped rather than passed on. No `@`, no whitespace, no percent escapes: those are the shapes
 * an identifier arrives in.
 */
const PLACEMENT_REF = /^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/;

export function placementRef(value: string | null): string | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_PLACEMENT_REF) return undefined;
  return PLACEMENT_REF.test(trimmed) ? trimmed : undefined;
}

/**
 * Where the interaction was shown.
 *
 * The page url is stripped of its query string, fragment and credentials before it goes anywhere,
 * because publisher urls routinely carry newsletter recipient ids and campaign parameters that
 * identify a person.
 */
export function buildContext(
  win: Window,
  articleId: string | null,
  ref: string | null = null,
): PageContext {
  const doc = win.document;
  const pageUrl = sanitisePageUrl(doc.location?.href ?? '');
  const host = referrerHost(doc.referrer ?? '');

  const lang = doc.documentElement?.lang || win.navigator?.language || '';
  const locale = lang.length > 0 && lang.length <= MAX_LOCALE ? lang : undefined;

  const width = win.innerWidth;
  const viewport =
    typeof width === 'number' && Number.isFinite(width)
      ? Math.min(20000, Math.max(0, Math.round(width)))
      : undefined;

  const article =
    articleId !== null && articleId.length > 0 && articleId.length <= MAX_ARTICLE_ID
      ? articleId
      : undefined;

  const placement = placementRef(ref);

  return {
    page_url: pageUrl,
    page_type: 'article',
    ...(article === undefined ? {} : { article_id: article }),
    ...(host === undefined ? {} : { referrer_host: host }),
    ...(locale === undefined ? {} : { locale }),
    ...(viewport === undefined ? {} : { viewport_width: viewport }),
    ...(placement === undefined ? {} : { placement_ref: placement }),
  };
}
